import { useEffect, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTabNavigate } from "@/app-shell";
import { useQuickbooksAccess } from "./quickbooks/use-quickbooks-access";
import { useReceiptsEnabled } from "./use-receipts-enabled";
import { AP_TAB, type ApTabValue, apListPath, parseApTab } from "./utils";

export const useApTabRoute = () => {
  const navigate = useNavigate();
  const tabNavigate = useTabNavigate();
  const { tab: routedTab } = useParams<{ tab?: string }>();
  const [searchParams] = useSearchParams();
  const legacyTab = parseApTab(searchParams.get("tab"));
  const receiptsEnabled = useReceiptsEnabled();
  const { isVisible: quickbooksVisible } = useQuickbooksAccess();
  const requestedTab =
    parseApTab(routedTab ?? null) ?? legacyTab ?? AP_TAB.review;
  const activeTab =
    (requestedTab === AP_TAB.receipts && !receiptsEnabled) ||
    (requestedTab === AP_TAB.quickbooks && !quickbooksVisible)
      ? AP_TAB.review
      : requestedTab;

  const searchWithoutTab = useMemo(() => {
    const next = new URLSearchParams(searchParams);
    next.delete("tab");
    const query = next.toString();
    return query ? `?${query}` : "";
  }, [searchParams]);

  useEffect(() => {
    if (!searchParams.has("tab")) return;
    void navigate(
      { pathname: apListPath(legacyTab), search: searchWithoutTab },
      { replace: true },
    );
  }, [searchParams, legacyTab, searchWithoutTab, navigate]);

  const goToTab = (tab: ApTabValue) =>
    tabNavigate({ pathname: apListPath(tab), search: searchWithoutTab });

  return { activeTab, receiptsEnabled, goToTab };
};
